import React, { useState, useEffect } from 'react';
import Button from '../ui-kit/Button';
import AdminScheduleModal from './AdminScheduleModal';
import AdminSlotDetailsModal from './AdminSlotDetailsModal';
import useScheduleStore from '../../stores/scheduleStore';
import useGroupsStore from '../../stores/groupsStore';
import useStudentsStore from '../../stores/studentsStore';
import { getWeeks } from '../../api';
import { FaEdit } from 'react-icons/fa';
import Sidebar from './Sidebar';

const days = ['Понедельник','Вторник','Среда','Четверг','Пятница','Суббота','Воскресенье'];
const displayDays = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

// Функции для работы с месяцами
const getMonthsList = () => {
  const months = [];
  const now = new Date();
  
  for (let i = -2; i <= 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }),
      year: date.getFullYear(),
      month: date.getMonth() + 1
    });
  }
  return months;
};

const getWeeksInMonth = (year, month) => {
  const weeks = [];
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  
  // Находим понедельник первой недели
  const startDate = new Date(firstDay);
  const dayOfWeek = startDate.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startDate.setDate(startDate.getDate() - daysToSubtract);
  
  let currentWeekStart = new Date(startDate);
  
  while (currentWeekStart <= lastDay) {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    weeks.push({
      start: new Date(currentWeekStart),
      end: new Date(weekEnd),
      dates: Array.from({ length: 7 }, (_, i) => {
        const date = new Date(currentWeekStart);
        date.setDate(date.getDate() + i);
        return date;
      })
    });
    
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  
  return weeks;
};

function AdminSchedule({ groupId, studentId, weekCount = 10 }) {
  const {
    fetchGroupSchedule,
    saveGroupSchedule,
    groupSchedule,
    fetchStudentSchedule,
    saveStudentSchedule,
    studentSchedule,
    fetchActualStudentSchedule,
    actualSchedule,
    loading,
    deleteStudentSchedule
  } = useScheduleStore();
  const { groups, fetchGroups } = useGroupsStore();
  const { students, fetchStudents } = useStudentsStore();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [modal, setModal] = useState({ open: false, dayIdx: null, weekIdx: null });
  const [details, setDetails] = useState({ open: false, slot: null });
  const [monthlySchedule, setMonthlySchedule] = useState([]);
  const [months] = useState(getMonthsList());

  // Получаем недели выбранного месяца
  const selectedMonthData = months.find(m => m.value === selectedMonth);
  const weeksInMonth = selectedMonthData ? getWeeksInMonth(selectedMonthData.year, selectedMonthData.month) : [];

  // Загружаем группы и студентов
  useEffect(() => {
    fetchGroups();
    fetchStudents();
  }, [fetchGroups, fetchStudents]);

  const loadMonthlySchedule = async () => {
    if (!selectedMonthData) return;
    const monthData = [];
    for (let weekIdx = 0; weekIdx < weeksInMonth.length; weekIdx++) {
      const weekData = Array.from({ length: 7 }, () => []);
      const weekStart = weeksInMonth[weekIdx].start;
      try {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        const startDateStr = weekStart.toISOString().slice(0, 10);
        const endDateStr = weekEnd.toISOString().slice(0, 10);
        
        let scheduleData = [];
        if (studentId && groupId) {
          scheduleData = await fetchActualStudentSchedule({ studentId, groupId, startDate: startDateStr, endDate: endDateStr });
        } else if (studentId) {
          // Итоговое расписание: индивидуальные + все групповые, где участвует ученик
          scheduleData = await fetchActualStudentSchedule({ studentId, startDate: startDateStr, endDate: endDateStr });
        } else if (groupId) {
          scheduleData = await fetchGroupSchedule({ groupId, startDate: startDateStr, endDate: endDateStr });
        }
        
        // Обрабатываем данные в зависимости от их формата
        if (groupId && Array.isArray(scheduleData)) {
          // getGroupSchedule возвращает массив документов Schedule
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          const weekEndStr = weekEnd.toISOString().slice(0, 10);
          
          scheduleData.forEach((scheduleDoc, docIdx) => {
            const dayIdx = days.indexOf(scheduleDoc.dayOfWeek);
            if (dayIdx !== -1) {
              const weekDayDate = new Date(weekStart);
              weekDayDate.setDate(weekStart.getDate() + dayIdx);
              const expectedDate = weekDayDate.toISOString().slice(0, 10);
              
              // Проверяем, что дата занятия попадает в текущую неделю
              const scheduleDate = scheduleDoc.date;
              
              if (scheduleDate >= startDateStr && scheduleDate <= weekEndStr) {
                const enrichedSlots = (scheduleDoc.timeSlots || []).map((s, idx) => ({
                  ...s,
                  scheduleId: scheduleDoc._id,
                  groupId: scheduleDoc.group || groupId,
                  date: scheduleDoc.date || expectedDate,
                  slotIndex: typeof s.slotIndex === 'number' ? s.slotIndex : idx
                }));
                weekData[dayIdx] = enrichedSlots.sort((a, b) => {
                  const timeA = a.startTime || '00:00';
                  const timeB = b.startTime || '00:00';
                  return timeA.localeCompare(timeB);
                });
              }
            }
          });
        } else {
          // getActualStudentSchedule возвращает массив дней с timeSlots
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          const weekEndStr = weekEnd.toISOString().slice(0, 10);
          (scheduleData || []).forEach(day => {
            const dayIdx = days.indexOf(day.dayOfWeek);
            if (dayIdx !== -1) {
              const weekDayDate = new Date(weekStart);
              weekDayDate.setDate(weekStart.getDate() + dayIdx);
              const expectedDate = weekDayDate.toISOString().slice(0, 10);
              
              // Проверяем, что дата занятия попадает в текущую неделю
              const scheduleDate = day.date;
              
              if (scheduleDate >= startDateStr && scheduleDate <= weekEndStr) {
                const enrichedSlots = (day.timeSlots || []).map((s, idx) => ({
                  ...s,
                  scheduleId: day._id,
                  groupId: day.group || groupId,
                  date: day.date || expectedDate,
                  slotIndex: typeof s.slotIndex === 'number' ? s.slotIndex : idx
                }));
                weekData[dayIdx] = enrichedSlots.sort((a, b) => {
                  const timeA = a.startTime || '00:00';
                  const timeB = b.startTime || '00:00';
                  return timeA.localeCompare(timeB);
                });
              }
            }
          });
        }
        
        monthData.push(weekData);
      } catch (error) {
        console.error('[AdminSchedule] Ошибка загрузки расписания недели', error);
      }
    }
    setMonthlySchedule(monthData);
  };

  useEffect(() => {
    loadMonthlySchedule();
  }, [groupId, studentId, selectedMonth, selectedMonthData, weeksInMonth.length]);

// Функция для получения номера недели в году
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};



  const handleAddSlot = (dayIdx, weekIdx, currentDate) => {
    setModal({ open: true, dayIdx, weekIdx, currentDate, freeTime: true });
  };

  const handleEditSlot = (dayIdx, slot, weekIdx, currentDate) => {
    setModal({ open: true, dayIdx, weekIdx, currentDate, initialData: slot, freeTime: true });
  };

    const handleDeleteSlot = async (dayIdx, weekIdx, slotToDelete = null, currentDate = null) => {
    const slot = slotToDelete;
    if (!slot) return;
    if (!window.confirm('Удалить это занятие?')) return;

    if (!currentDate) {
      // Fallback: вычисляем дату если не передана
      const weekStart = weeksInMonth[weekIdx]?.start;
      if (!weekStart) return;
      currentDate = new Date(weekStart);
      currentDate.setDate(weekStart.getDate() + dayIdx);
    }
    
    const dateStr = currentDate.toISOString().slice(0, 10);
    const week = getWeekNumber(currentDate);
    const dayOfWeek = days[dayIdx];
    const startTime = slot.startTime;
    const endTime = slot.endTime;
    
    if (slot.student) {
      await deleteStudentSchedule({ studentId, week, dayOfWeek, date: dateStr });
    } else if (groupId) {
      if (studentId && slot.students && Array.isArray(slot.students) && slot.students.includes(studentId)) {
      await saveGroupSchedule({
        groupId: groupId,
        week: week,
        dayOfWeek: dayOfWeek,
        date: dateStr,
        startTime: startTime,
        endTime: endTime,
        studentIdToRemove: studentId,
      });
      } else if (!studentId) {
        const currentDay = monthlySchedule[weekIdx]?.[dayIdx] || [];
        const newTimeSlots = currentDay.filter(s => !(s.startTime === startTime && s.endTime === endTime));
        await saveGroupSchedule({ groupId, week, dayOfWeek, date: dateStr, timeSlots: newTimeSlots });
        
        // Обновляем локальное состояние
        const updatedMonthData = [...monthlySchedule];
        updatedMonthData[weekIdx][dayIdx] = newTimeSlots;
        setMonthlySchedule(updatedMonthData);
      return;
    }
    } else {
      alert('Удаление невозможно: слот не привязан к этому ученику.');
      return;
    }
    
    // Обновляем локальное состояние после удаления
    const updatedMonthData = [...monthlySchedule];
    const currentDay = updatedMonthData[weekIdx]?.[dayIdx] || [];
    updatedMonthData[weekIdx][dayIdx] = currentDay.filter(s => !(s.startTime === startTime && s.endTime === endTime));
    setMonthlySchedule(updatedMonthData);
  };
  

  const EMPTY_SLOT = { startTime: '', endTime: '', subject: null, comment: '' };

  const handleSaveSlot = async (data) => {
    const weekIdx = modal.weekIdx;
    const dayOfWeek = days[modal.dayIdx];
    
    // Используем переданную дату напрямую
    if (!modal.currentDate) {
      alert('Ошибка: не удалось определить дату');
      return;
    }
    
    const dateStr = modal.currentDate.toISOString().slice(0, 10);
    const week = 1; // Фиксированное значение для совместимости
    const startTime = data.customStartTime;
    const endTime = data.customEndTime;
    
    if (!startTime || !endTime) {
      alert('Пожалуйста, укажите время начала и окончания занятия');
      return;
    }
    
    const slot = {
      subject: data.subjectId,
      startTime,
      endTime,
      comment: data.comment,
      students: data.students && data.students.length ? data.students : (data.studentId ? [data.studentId] : (studentId ? [studentId] : []))
    };
    
    const currentDaySlots = monthlySchedule[weekIdx]?.[modal.dayIdx] || [];
    let timeSlotsArr;
    
    if (modal.initialData) {
      // Редактируем существующий слот
      timeSlotsArr = currentDaySlots.map(s => 
        s.startTime === modal.initialData.startTime && s.endTime === modal.initialData.endTime ? slot : s
      );
    } else {
      // Добавляем новый слот
      timeSlotsArr = [...currentDaySlots, slot];
    }
    
    timeSlotsArr = timeSlotsArr.filter(s => s && s.subject && s.startTime && s.endTime);
    
    // Определяем студентов для сохранения
    let studentsToSave = [];
    
    if (Array.isArray(data.students) && data.students.length) {
      // Если студенты выбраны в модалке, используем их
      studentsToSave = data.students;
    } else if (groupId) {
      // Если групповое занятие без выбранных студентов, берем всех студентов группы
      studentsToSave = students.filter(s => {
        if (Array.isArray(s.groups)) {
          return s.groups.some(g => String(g._id || g) === String(groupId));
        } else if (s.group) {
          return String(s.group._id || s.group) === String(groupId);
        }
        return false;
      }).map(s => s._id);
    }
    
    // Сохранение
    if (Array.isArray(data.students) && data.students.length && groupId && !studentId) {
      await saveGroupSchedule({ groupId, week, dayOfWeek, date: dateStr, timeSlots: timeSlotsArr, students: data.students });
    } else if (Array.isArray(data.students) && data.students.length && (studentId || data.studentId)) {
      for (const sid of data.students) {
        await saveStudentSchedule({ studentId: sid, week, dayOfWeek, date: dateStr, timeSlots: timeSlotsArr, students: [sid] });
      }
    } else if (Array.isArray(data.students) && data.students.length && !groupId && !studentId) {
      for (const sid of data.students) {
        await saveStudentSchedule({ studentId: sid, week, dayOfWeek, date: dateStr, timeSlots: timeSlotsArr, students: [sid] });
      }
    } else if (studentId || data.studentId) {
      const sid = data.studentId || studentId;
      await saveStudentSchedule({ studentId: sid, week, dayOfWeek, date: dateStr, timeSlots: timeSlotsArr, students: [sid] });
    } else if (groupId) {
      // Групповое занятие - принудительно передаем студентов группы
      const groupStudents = students.filter(s => {
        const belongs = Array.isArray(s.groups) 
          ? s.groups.some(g => String(g._id || g) === String(groupId))
          : String(s.group?._id || s.group) === String(groupId);
        return belongs;
      }).map(s => s._id);
      await saveGroupSchedule({ 
        groupId, 
        week, 
        dayOfWeek, 
        date: dateStr, 
        timeSlots: timeSlotsArr, 
        students: groupStudents 
      });
    }
    
    // Принудительно перезагружаем данные месяца (не полагаемся на локальное обновление)
    
    // Даем время серверу сохранить
    await loadMonthlySchedule();
    
    setModal({ open: false, dayIdx: null, weekIdx: null });
  };

  const handleShowDetails = (slot) => {
    setDetails({ open: true, slot });
  };

  // Вынесенные функции для получения имени группы и ученика
  const getGroupName = (groupId) => {
    const group = groups.find(g => String(g._id) === String(groupId));
    return group ? group.name : 'Группа';
  };
  const getStudentName = (studentId) => {
    const student = students.find(s => String(s._id) === String(studentId));
    return student ? student.fullName : 'Ученик';
  };

  // Перед рендером

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ width: '100%', overflowX: 'auto', minHeight: '100vh', position: 'relative' }}>
        {/* Тень справа от Sidebar только на десктопе */}
        {window.innerWidth > 900 && (
          <div style={{ position: 'fixed', left: 200, top: 0, width: 16, height: '100vh', zIndex: 11, pointerEvents: 'none' }} />
        )}
      <div className="adminschedule-header">
        <span className="adminschedule-header-label">Месяц:</span>
        <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="adminschedule-select">
          {months.map((month) => (
            <option value={month.value} key={month.value}>{month.label}</option>
          ))}
        </select>
      </div>
      <div className='AdminSchedule-monthly'>
        {weeksInMonth.map((week, weekIdx) => (
          <div key={weekIdx} className='monthly-week' style={{ marginBottom: 30 }}>
            <div className='week-header' style={{ fontSize: 14, color: '#666', marginBottom: 10, textAlign: 'center' }}>
              Неделя {week.start.getDate()}.{String(week.start.getMonth() + 1).padStart(2, '0')} - {week.end.getDate()}.{String(week.end.getMonth() + 1).padStart(2, '0')}
            </div>
            <div className='AdminSchedule-kanban' style={{ display: 'flex', gap: 15 }}>
              {displayDays.map((day, dayIdx) => {
                const currentDate = week.dates[dayIdx];
                const isCurrentMonth = currentDate.getMonth() === selectedMonthData?.month - 1;
                
                return (
                  <div className='Schedule__column' key={dayIdx} style={{ 
                    flex: 1, 
                    opacity: isCurrentMonth ? 1 : 0.3,
                    minHeight: 200
                  }}>
                    <div className='adminschedule-title' style={{ 
                      fontSize: 13, 
                      marginBottom: 8,
                      color: isCurrentMonth ? '#333' : '#999'
                    }}>
                      {day} {currentDate.getDate()}.{String(currentDate.getMonth() + 1).padStart(2, '0')}
                    </div>
                    
                    {/* Отображаем только добавленные занятия */}
                    {(() => {
                      const daySlots = monthlySchedule[weekIdx]?.[dayIdx] || [];
                      return daySlots;
                    })().map((slot, slotIdx) => (
                      <div className='ScheduleBlock filled' key={slotIdx} style={{ marginBottom: 8, fontSize: 14 }}>
                        <div className="scheduleblock-content">
                          <div className="scheduleblock-time" style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
                            {slot.startTime}{slot.endTime ? `–${slot.endTime}` : ''}
                          </div>
                          {(() => {
                            // Логика подписи: если это групповое занятие — показываем название группы, иначе ученика
                            if (slot.groupId) {
                              return <div className="scheduleblock-group" style={{ fontSize: 13, marginBottom: 2 }}>Группа: {getGroupName(slot.groupId)}</div>;
                            }
                            if (slot.student) {
                              const sid = (slot.students && slot.students[0]) || studentId;
                              return <div className="scheduleblock-group" style={{ fontSize: 13, marginBottom: 2 }}>Ученик: {getStudentName(sid)}</div>;
                            }
                            if (Array.isArray(slot.students)) {
                              if (slot.students.length > 1) {
                                return <div className="scheduleblock-group" style={{ fontSize: 13, marginBottom: 2 }}>Группа: {getGroupName(slot.groupId || groupId)}</div>;
                              } else if (slot.students.length === 1) {
                                return <div className="scheduleblock-group" style={{ fontSize: 13, marginBottom: 2 }}>Ученик: {getStudentName(slot.students[0])}</div>;
                              }
                            }
                            // На странице группы выводим её название по пропу
                            if (!studentId && groupId) {
                              return <div className="scheduleblock-group" style={{ fontSize: 13, marginBottom: 2 }}>Группа: {getGroupName(groupId)}</div>;
                            }
                            return null;
                          })()}
                          <div className="scheduleblock-subject" style={{ fontSize: 13, marginTop: 2 }}>Предмет: {slot.subject?.name || slot.subject}</div>
                          {slot.comment && <div className="scheduleblock-comment" style={{ fontSize: 12, marginTop: 2 }}>Комментарий: {slot.comment}</div>}
                        </div>
                        <div className="scheduleblock-actions" style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', marginTop: 8 }}>
                          <button className="scheduleblock-edit-btn" style={{ fontSize: 12, padding: '6px 10px', width: '100%' }} onClick={() => handleEditSlot(dayIdx, slot, weekIdx, currentDate)}>Ред.</button>
                          <button className="scheduleblock-details-btn" style={{ fontSize: 12, padding: '6px 10px', width: '100%' }} onClick={() => handleShowDetails({ ...slot })}>Подр.</button>
                          {(studentId
                            ? (slot.student || (slot.students && Array.isArray(slot.students) && slot.students.includes(studentId)))
                            : !!groupId
                          ) && (
                            <button className="scheduleblock-delete-btn" style={{ fontSize: 12, padding: '6px 10px', width: '100%' }} onClick={() => handleDeleteSlot(dayIdx, weekIdx, slot, currentDate)}>Удал.</button>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Плюсик для добавления нового занятия */}
                    {(!!groupId || !!studentId) && isCurrentMonth && (
                      <div className='ScheduleBlock empty'>
                        <button className="add-slot-btn" style={{ fontSize: 16 }} onClick={() => handleAddSlot(dayIdx, weekIdx, currentDate)}>+</button>
                      </div>
                  )}
                </div>
              );
            })}
              </div>
          </div>
        ))}
      </div>
      <AdminScheduleModal
        open={modal.open}
        onClose={() => setModal({ open: false, dayIdx: null, weekIdx: null, initialData: {} })}
        onSave={handleSaveSlot}
        initialData={modal.initialData}
        groupId={groupId}
        studentId={studentId}
        freeTime={modal.freeTime}
      />
      <AdminSlotDetailsModal
        open={details.open}
        onClose={() => setDetails({ open: false, slot: null })}
        slot={details.slot}
      />
      </div>
    </div>
  );
}

export default AdminSchedule; 